import PropTypes from 'prop-types';
import { closeDatetimePickers } from '../../../hooks/useDatetimeKeyboardNav';
import IconTooltip, { ICON_TOOLTIP_HOST } from "../../UI/Tooltip/IconTooltip";

// react-datetime opens on input click (`_onInputClick`). Any other picker that
// is still open has already been dismissed by the shared `focusin` handler in
// useDatetimeKeyboardNav — focus lands on this button before the click fires,
// and that handler closes every picker the focused element isn't inside. The
// old ad-hoc "dismiss others when one opens" workaround here is therefore
// redundant, and only ever covered the open path anyway.
const openPicker = (inputRef) => {
    const input = inputRef.current;
    if (!input) return;
    input.click();
};

const focusInsidePicker = (inputRef) => {
    setTimeout(() => {
        const wrapper = inputRef.current?.closest('.rdt');
        if (!wrapper?.classList.contains('rdtOpen')) return;
        const target = wrapper.querySelector('.rdtPicker td[tabindex="0"]') || wrapper.querySelector('.rdtCounter');
        target?.focus({ preventScroll: true });
    }, 0);
};

const togglePicker = (isPickerOpen, inputRef, moveFocusInside) => {
    if (isPickerOpen) {
        closeDatetimePickers();
        return;
    }
    openPicker(inputRef);
    if (moveFocusInside) focusInsidePicker(inputRef);
};

const handlePickerKeyDown = (event, isPickerOpen, inputRef, onKeyDown) => {
    const input = event.currentTarget;
    const wholeTextSelected = !input.value || (input.selectionStart === 0 && input.selectionEnd === input.value.length);
    const toggles = event.key === 'Enter' || (event.key === ' ' && wholeTextSelected);
    if (toggles && !event.altKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        togglePicker(isPickerOpen, inputRef, true);
        return;
    }
    onKeyDown?.(event);
};

export const DatePicker = (props) => {
    const { openPickerAriaLabel, isPickerOpen, fieldLabel, onKeyDown, onFocus, ...inputProps } = props;
    const pickerLabel = openPickerAriaLabel || "Open date picker";
    return (
        <div className="form-group custom-tooltip tooltip-auto-width center-tooltip top-tooltip m-hide">
            <div className="form-group-in with-icon">
                <input className="form-control form-control-grey height-46 focus-font-bold" {...inputProps} data-testid='datePicker' ref={props.ref} aria-label={fieldLabel || pickerLabel} onKeyDown={(event) => handlePickerKeyDown(event, isPickerOpen, props.ref, onKeyDown)} />
                <button type="button" className="btn-unstyled form-group-icon font-size-18" aria-label={fieldLabel ? `${pickerLabel}, ${fieldLabel}` : pickerLabel} aria-expanded={!!isPickerOpen} onClick={() => togglePicker(isPickerOpen, props.ref, true)}>
                    <span className="icon-calendar" aria-hidden="true"></span>
                </button>
            </div>
            <div className="custom-tooltip-content text-nowrap text-center">
                <div className="custom-tooltip-in">
                    <p>{props.value}</p>
                </div>
            </div>
        </div>
    )
}

DatePicker.propTypes = {
    value: PropTypes.string.isRequired,
    ref: PropTypes.object
};

export const Timepicker = (props) => {
    const { openPickerAriaLabel, isPickerOpen, fieldLabel, onKeyDown, onFocus, ...inputProps } = props;
    const pickerLabel = openPickerAriaLabel || "Open time picker";
    return (
        <div className="form-group">
            <div className="form-group-in with-icon">
                <input className="form-control form-control-grey height-46 focus-font-bold"  {...inputProps} data-testid='timePicker' ref={props.ref} aria-label={fieldLabel || pickerLabel} onKeyDown={(event) => handlePickerKeyDown(event, isPickerOpen, props.ref, onKeyDown)} />
                <button type="button" className={`btn-unstyled form-group-icon font-size-18 ${ICON_TOOLTIP_HOST}`} aria-label={fieldLabel ? `${pickerLabel}, ${fieldLabel}` : pickerLabel} aria-expanded={!!isPickerOpen} onClick={() => togglePicker(isPickerOpen, props.ref, true)}>
                    <span className="icon-time-clock" aria-hidden="true"></span>
                    <IconTooltip label={pickerLabel} />
                </button>
            </div>
        </div>
    )
}

Timepicker.propTypes = {
    value: PropTypes.string.isRequired,
    ref: PropTypes.object
};

export const renderDay = (props, currentDate, selectedDate) => {
    const classes = props.className || '';
    return (
        <td
            {...props}
            aria-label={currentDate.format('dddd, D MMMM YYYY')}
            aria-selected={classes.includes('rdtActive')}
            aria-current={classes.includes('rdtToday') ? 'date' : undefined}
            aria-disabled={classes.includes('rdtDisabled') || undefined}
        >
            {currentDate.date()}
        </td>
    );
}

export const renderMonth = (props, month, year, selectedDate) => {
    const shortMonthList = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return <td {...props}>{shortMonthList[month]}</td>;
}

export const renderYear = (props, year, selectedDate) => {
    return <td {...props}>{year}</td>;
}
